import React, {useState} from 'react';
import {useForm} from 'react-hook-form';
import {Dialog, DialogTitle, DialogContent, DialogActions, Button} from '@mui/material';
import {passwordChangeRequestSchema, PasswordChangeSchema} from "../../schemas/auth/passwordChangeSchema";
import {zodResolver} from "@hookform/resolvers/zod";
import PasswordTextField from "../input/PasswordTextField";
import {postFetcher} from "../../utils/api/fetchers";
import {useNotification} from "../../utils/hooks/useNotification";

interface PasswordChangeDialogProps {
    open: boolean;
    onClose: () => void;
}

const PasswordChangeDialog: React.FC<PasswordChangeDialogProps> = ({open, onClose}) => {
    const {register, handleSubmit, formState: {errors}} = useForm<PasswordChangeSchema>({
        resolver: zodResolver(passwordChangeRequestSchema),
    });
    const {showNotification} = useNotification();

    const onSubmit = async (data: PasswordChangeSchema) => {
        postFetcher('/auth/change-password', data)
            .then(() => showNotification('Heslo bylo úspěšně změněno', 'success'))
            .catch((error) => {
                console.error(error);
                if (error?.status === 400) {
                    showNotification('Změna hesla selhala, špatné staré heslo nebo stejné jako nové', 'error');
                } else {
                    showNotification('Změna hesla selhala', 'error');
                }
            });
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle sx={{fontWeight: 'bold'}}>
                Změna hesla
            </DialogTitle>
            <DialogContent>
                <form onSubmit={handleSubmit(onSubmit)} style={{padding: '0.5 1rem'}}>
                    <PasswordTextField
                        variant="outlined"
                        label="Staré heslo"
                        type="password"
                        fullWidth
                        margin="normal"
                        autoFocus
                        {...register('oldPassword', {required: 'Zadejte staré heslo'})}
                        error={!!errors.oldPassword}
                        helperText={errors.oldPassword?.message}
                    />
                    <PasswordTextField
                        variant="outlined"
                        label="Nové heslo"
                        type="password"
                        fullWidth
                        margin="normal"
                        {...register('newPassword', {required: 'Zadejte nové heslo'})}
                        error={!!errors.newPassword}
                        helperText={errors.newPassword?.message}
                    />
                    <PasswordTextField
                        variant="outlined"
                        label="Potvrzení hesla"
                        type="password"
                        fullWidth
                        margin="normal"
                        {...register('confirmPassword')}
                        error={!!errors.confirmPassword}
                        helperText={errors.confirmPassword?.message}
                    />
                </form>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary">
                    Zrušit
                </Button>
                <Button onClick={handleSubmit(onSubmit)} color="primary" variant="contained">
                    Potvrdit
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const usePasswordChangeDialog = () => {
    const [open, setOpen] = useState(false);

    const openDialog = () => setOpen(true);
    const closeDialog = () => setOpen(false);

    const PasswordChangeDialogComponent = () => (
        <PasswordChangeDialog open={open} onClose={closeDialog}/>
    );

    return {openDialog, closeDialog, PasswordChangeDialogComponent};
};

export default usePasswordChangeDialog;
